import { useEffect, useState } from 'react';
import { ClientSideStorageType } from '../../../../models/client-side-storage-type.enum';
import { ClientSideStorageService } from '../../../../services/client-side-storage.service';
import ValidationInfo from '../../../../validation/validation-info';
import FocusTrap from '../../../Shared/FocusTrap';
import './style.scss';
import CheckboxFormGroup from '../../../Shared/CheckboxFormGroup';
import { SanitizerService } from '../../../../services/sanitizer.service';
import { useTexts } from '../../../../hooks/texts.hook';
import { ICookieConsentModalTexts } from '../..';
import { callApiEndpoint } from '../../helpers/cookie-consent.helper';

enum CookieType {
  necessary = 'necessary',
  functional = 'functional',
  analytics = 'analytics',
}

function getAllowedCookieTypesFromClientSideStorage(): CookieType[] {
  const cookieTypes: CookieType[] = [CookieType.necessary];

  if (ClientSideStorageService.isConsentGiven(ClientSideStorageType.Functional)) {
    cookieTypes.push(CookieType.functional);
  }
  if (ClientSideStorageService.isConsentGiven(ClientSideStorageType.Analytics)) {
    cookieTypes.push(CookieType.analytics);
  }

  return cookieTypes;
}

function CookieConsentSettingsModal({ show, onClose, texts }: { show: boolean; onClose: () => void; texts: ICookieConsentModalTexts }) {
  // === HOOKS ===
  const { t } = useTexts();

  const [selectedOption, setSelectedOption] = useState<CookieType[]>(getAllowedCookieTypesFromClientSideStorage());

  useEffect(() => {
    if (show) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedOption(getAllowedCookieTypesFromClientSideStorage());
    }
  }, [show]);

  function handleClose() {
    if (!ClientSideStorageService.isConsentStatusSet()) {
      document.dispatchEvent(new CustomEvent('show-cookie-consent-modal'));
    }
    onClose();
  }

  async function handleApproveAllClick() {
    const success = ClientSideStorageService.approveAll();

    if (success) {
      document.dispatchEvent(new CustomEvent('consent-status-approved'));
      document.dispatchEvent(new CustomEvent('hide-cookie-consent-modal'));
    }
    onClose();

    await callApiEndpoint('all', success);
  }

  async function handleApproveNecessaryClick() {
    const success = ClientSideStorageService.approveNecessary();

    if (success) {
      document.dispatchEvent(new CustomEvent('consent-status-approved'));
      document.dispatchEvent(new CustomEvent('hide-cookie-consent-modal'));
    }
    onClose();

    await callApiEndpoint('necessary', success);
  }

  async function handleSaveClick() {
    let consentStatus: ClientSideStorageType = ClientSideStorageType.Necessary;

    selectedOption.forEach((option) => {
      if (option === CookieType.functional) {
        consentStatus |= ClientSideStorageType.Functional;
      } else if (option === CookieType.analytics) {
        consentStatus |= ClientSideStorageType.Analytics;
      }
    });

    const success = ClientSideStorageService.setConsentStatus(consentStatus);

    if (success) {
      document.dispatchEvent(new CustomEvent('consent-status-approved'));
      document.dispatchEvent(new CustomEvent('hide-cookie-consent-modal'));
    }
    onClose();

    await callApiEndpoint('custom', success);
  }

  // XHTML backend
  const functionalCookiesHelpText = SanitizerService.sanitizeHtml(texts.functionalcookieshelptext);

  return (
    <>
      {show && (
        <div>
          <FocusTrap isVisible={show} onEscape={handleClose}>
            <div className="modal d-block fade show" aria-labelledby="cookie-consent-settings-modal-title" aria-modal="true" role="dialog">
              <div className="modal-dialog modal-dialog-centered cookie-consent-settings-modal__modal-dialog">
                <div className="modal-content">
                  <div className="modal-header pb-0">
                    <h1 id="cookie-consent-settings-modal-title" className="modal-title h3">
                      {t('Anpassa cookieinställningar', 'Adjust cookie settings')}
                    </h1>
                    <button type="button" className="btn-close" aria-label={t('Stäng', 'Close')} onClick={handleClose}></button>
                  </div>
                  <div className="modal-body">
                    <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(texts.cookieconsenttext) }}></div>
                    <CheckboxFormGroup
                      id="cookie-consent-settings-modal-checkboxes"
                      labelText={t('Välj vilka kakor du vill tillåta', 'Select which cookies you want to allow')}
                      hideLabel={true}
                      rowLayoutMdUp={false}
                      wideGap={true}
                      options={[
                        { value: CookieType.necessary, text: t('Nödvändiga kakor (går inte att välja bort)', 'Necessary cookies (cannot be disabled)'), disabled: true },
                        {
                          value: CookieType.functional,
                          text: t('Funktionella kakor', 'Functional cookies'),
                          optionHelpTextElement:
                            functionalCookiesHelpText !== '' ? <div className="last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: functionalCookiesHelpText }}></div> : undefined,
                        },
                        {
                          value: CookieType.analytics,
                          text: t('Analyskakor', 'Analytics cookies'),
                          optionHelpTextElement: (
                            <ul className="mb-0">
                              <li>
                                {t('Gör det möjligt att analysera hur du använder hemsidan för att göra förbättringar', 'Enable us to analyse how you use the website in order to make improvements')}
                              </li>
                            </ul>
                          ),
                        },
                      ]}
                      state={{ value: selectedOption, validationInfo: new ValidationInfo() }}
                      onChange={(value: string[]) => setSelectedOption(value as CookieType[])}
                    ></CheckboxFormGroup>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary flex-fill cookie-consent-settings-modal__btn" onClick={handleApproveNecessaryClick}>
                      {t('Godkänn nödvändiga', 'Approve necessary')}
                    </button>
                    <button type="button" className="btn btn-secondary flex-fill mx-lg-2 my-2 my-lg-0 cookie-consent-settings-modal__btn" onClick={handleApproveAllClick}>
                      {t('Godkänn alla', 'Approve all')}
                    </button>
                    <button type="button" className="btn btn-primary flex-fill cookie-consent-settings-modal__btn" onClick={handleSaveClick}>
                      {t('Spara val', 'Save choices')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </FocusTrap>
        </div>
      )}
      {show && <div className="modal-backdrop fade show"></div>}
    </>
  );
}
export default CookieConsentSettingsModal;
